import React, { useEffect, useState } from 'react';
import { findCandidates, teachClaimSoftNumber, vendorForSpelling, loadVendorList, createCatalogItem } from './lineFix';

/*
  "WHICH ITEM IS THIS?", ordering stage 2, 11 October 2026.

  Shown inside the job window, in place of the line list, for one line OnHand
  does not know. It searches the catalog starting from the line's own number,
  with the ClaimSoft vendor's items first, and he picks one. **Then it asks**,
  because the answer is written onto the item record for good and every future
  transfer order will use it.
*/
export default function LinePicker({ line, userId, onDone, onBack }) {
  const claimsoftVendor = (line.source_row && line.source_row.Vendor) || '';
  const [text, setText] = useState(line.item_as_given);
  /* undefined while the ClaimSoft vendor is still being looked up, so the
     search waits for it instead of running twice. */
  const [vendorId, setVendorId] = useState(undefined);
  const [everywhere, setEverywhere] = useState(false);
  const [wide, setWide] = useState(false);
  const [found, setFound] = useState(null);
  const [failed, setFailed] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  /* Adding an item no vendor's file has, his word of 11 October 2026. */
  const [adding, setAdding] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({ vendorId: '', part: line.item_as_given, words: line.description || '' });

  useEffect(() => { vendorForSpelling(claimsoftVendor).then(v => setVendorId(v || null)); }, [claimsoftVendor]);

  useEffect(() => {
    if (vendorId === undefined) return undefined;
    let alive = true;
    setFound(null);
    const t = setTimeout(async () => {
      const r = await findCandidates({ number: line.item_as_given, text, vendorId, everywhere });
      if (!alive) return;
      if (r.error) { setFailed(r.error.message); setFound([]); return; }
      setFailed(null);
      setFound(r.items);
      setWide(r.searchedEverything);
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [text, vendorId, everywhere, line.item_as_given]);

  const save = async () => {
    setSaving(true);
    setProblem(null);
    const r = await teachClaimSoftNumber({ number: line.item_as_given, catalogItemId: chosen.id, userId });
    setSaving(false);
    if (!r.ok) { setProblem(r.error ? r.error.message : 'That did not save.'); return; }
    onDone(r);
  };

  const openAdd = async () => {
    setAdding(true);
    setProblem(null);
    setForm(f => ({ ...f, vendorId: f.vendorId || vendorId || '' }));
    if (!vendors.length) { const r = await loadVendorList(); setVendors(r.vendors); }
  };

  const addAndSave = async () => {
    setSaving(true);
    setProblem(null);
    const made = await createCatalogItem({ vendorId: form.vendorId, partNumber: form.part, description: form.words, fromClaimSoft: line.item_as_given });
    if (!made.ok) { setSaving(false); setProblem(made.error.message); return; }
    const r = await teachClaimSoftNumber({ number: line.item_as_given, catalogItemId: made.item.id, userId });
    setSaving(false);
    if (!r.ok) { setProblem('The item ' + made.item.oh_number + ' was added, but the ClaimSoft number could not be saved onto it: ' + (r.error ? r.error.message : 'unknown')); return; }
    onDone({ ...r, added: made.item.oh_number });
  };

  if (adding) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginBottom: 8 }}>
          <b>Add ClaimSoft <span className="rpnum">{line.item_as_given}</span> to the catalog</b>
          <button type="button" className="btn btn-quiet" disabled={saving} onClick={() => { setAdding(false); setProblem(null); }}>Back to the search</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
          For an item no vendor's file has. It is marked as added by MedEx by hand, so these can be found and checked later.
        </p>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Vendor</span>
          <select className="fld-sel" style={{ width: '100%' }} value={form.vendorId} onChange={e => setForm(f => ({ ...f, vendorId: e.target.value }))}>
            <option value="">Pick the vendor</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </label>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>The vendor's own part number</span>
          <input className="fld" style={{ width: '100%' }} value={form.part} onChange={e => setForm(f => ({ ...f, part: e.target.value }))} />
          <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            Filled in from ClaimSoft to start. Change it if the vendor writes it differently.
          </span>
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Description</span>
          <input className="fld" style={{ width: '100%' }} value={form.words} onChange={e => setForm(f => ({ ...f, words: e.target.value }))} />
        </label>
        {problem && <p style={{ color: 'var(--critical-ink)' }}>{problem}</p>}
        <button type="button" className="btn btn-primary" disabled={saving} onClick={addAndSave}>
          {saving ? 'Adding...' : 'Add the item and save'}
        </button>
      </div>
    );
  }

  if (chosen) {
    return (
      <div>
        <p style={{ marginTop: 0 }}>
          ClaimSoft <b className="rpnum">{line.item_as_given}</b> ({line.description || 'no description'}) is
        </p>
        <div className="card" style={{ padding: '12px 14px', marginBottom: 12 }}>
          <b>{chosen.vendor ? chosen.vendor.name : 'no vendor'}</b> <span className="rpnum">{chosen.vendor_part_number}</span>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{chosen.our_description || chosen.vendor_description}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{chosen.oh_number}</div>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          This is saved onto the item for good, so every transfer order carrying {line.item_as_given} will know it.
          Any other open job with this number is filled in too.
        </p>
        {problem && <p style={{ color: 'var(--critical-ink)' }}>{problem}</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-quiet" disabled={saving} onClick={() => { setChosen(null); setProblem(null); }}>Not this one</button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
            {saving ? 'Saving...' : 'Yes, save it'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginBottom: 8 }}>
        <div>
          <b>Which item is ClaimSoft <span className="rpnum">{line.item_as_given}</span>?</b>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
            {line.description || 'No description'}{claimsoftVendor ? ' · ClaimSoft says ' + claimsoftVendor : ''}
          </div>
        </div>
        <button type="button" className="btn btn-quiet" onClick={onBack}>Back to the lines</button>
      </div>
      <input className="fld" style={{ width: '100%', marginBottom: 10 }} value={text} autoFocus
        aria-label="Search the catalog" placeholder="A part number or words from the description"
        onChange={e => setText(e.target.value)} />
      {failed && <p style={{ color: 'var(--critical-ink)' }}>OnHand could not search just now: {failed}</p>}
      {found === null ? (
        <p style={{ color: 'var(--text-secondary)' }}>
          {everywhere ? 'Searching every vendor\'s items. This takes a few seconds...' : 'Looking...'}
        </p>
      ) : found.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>Nothing in the catalog matches that. Try a different number or a word from the description.</p>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {found.map(it => (
            <button key={it.id} type="button" className="btn btn-quiet"
              style={{ justifyContent: 'flex-start', textAlign: 'left', height: 'auto', padding: '8px 12px', whiteSpace: 'normal' }}
              onClick={() => setChosen(it)}>
              <span style={{ minWidth: 0 }}>
                <b>{it.vendor ? it.vendor.name : 'no vendor'}</b> <span className="rpnum">{it.vendor_part_number}</span>
                {vendorId && it.vendor_id === vendorId && (
                  <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 700, color: 'var(--success-ink)' }}>the vendor ClaimSoft names</span>
                )}
                <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {it.our_description || it.vendor_description || 'No description'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      {found !== null && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {!wide && (
            <button type="button" className="btn btn-quiet" onClick={() => setEverywhere(true)}>
              Not here? Search every vendor (a few seconds)
            </button>
          )}
          <button type="button" className="btn btn-quiet" onClick={openAdd}>Not in the catalog? Add it</button>
        </div>
      )}
    </div>
  );
}
